/**
 * React-hook-form *baseline* (defaultValues) management for the chat Settings
 * tab. Change detection (`dirtyFields`) is measured against the baseline, so
 * whether a save fires — and what a save marks clean afterwards — depends
 * entirely on where that baseline sits. These helpers keep it honest:
 *
 *  - `seedPriceVisibilityBaseline` moves the baseline of the price-override
 *    controls to server truth on load (via `resetField`, not `setValue`), so
 *    unchecking an existing override is genuinely dirty and change detection
 *    fires. `setValue(..., { shouldDirty: false })` only moved the VALUE and
 *    left the baseline at the mount default (`false`), which made
 *    unchecking-an-override a silent no-op.
 *  - `commitSubmittedFields` / `resetToSubmitted` baseline the values that were
 *    actually SENT (the `data` snapshot `handleSubmit` passed in) — never the
 *    live `getValues()`, which could have moved on. An edit typed mid-save
 *    (inputs stay enabled during a save) must remain VISIBLE and DIRTY
 *    afterwards, not be baptized as the clean saved baseline.
 *
 * A plain `reset(data)` / `resetField(name, { defaultValue })` cannot express
 * "re-baseline to the submitted value but keep an edit typed since": RHF always
 * overwrites the live value with the new default. And `reset(data, {
 * keepDirtyValues: true })` keeps the value but never recomputes `dirtyFields`,
 * so saved fields would stay dirty and re-fire on the next Save. So we
 * re-baseline to the submitted value and then re-apply (with `shouldDirty`)
 * only the fields whose live value has diverged from what was sent.
 *
 * They take `UseFormReturn` as their only seam, so the RHF interplay is unit
 * testable against a real `useForm` without the surrounding page.
 */

import { ChatSettingsFormData } from '@choco-admin/types';
import { UseFormReturn } from 'react-hook-form';

type SettingsForm = UseFormReturn<ChatSettingsFormData>;
type SettingsFieldName = keyof ChatSettingsFormData;
type SettingsFieldValue = ChatSettingsFormData[SettingsFieldName];

/** Every field the Settings form manages (used to scan for mid-save edits). */
const SETTINGS_FIELD_NAMES: SettingsFieldName[] = [
  'chatName',
  'restaurantName',
  'deliveryAddress',
  'customerNumber',
  'additionalEmails',
  'chatType',
  'subCatalog',
  'createdContext',
  'customerLevelMovAmount',
  'customerLevelMovCurrency',
  'visibleChatPrices',
  'isPriceOverrideEnabled',
  'isEcommercePoNumberRequired',
];

/**
 * Structural equality for form values (strings, booleans, null, and the
 * select-option objects/arrays). Field values are plain JSON, so a serialized
 * compare is exact and avoids a hand-rolled recursive walk.
 */
const valuesEqual = (a: SettingsFieldValue, b: SettingsFieldValue): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/** The subset of the server prices-visibility read used to seed the baseline. */
export interface PricesVisibilityBaseline {
  displayPricesOverride?: boolean | null;
}

/**
 * Seeds the RHF baseline (and value) of the price-override controls from the
 * server read.
 *
 * Only seeds when the operator has NOT already edited the controls, so a
 * background refetch (e.g. after a successful price save) never clobbers an
 * in-progress edit — mirroring the original `shouldDirty: false` intent.
 */
export const seedPriceVisibilityBaseline = (
  formMethods: SettingsForm,
  pricesVisibility: PricesVisibilityBaseline
): void => {
  const { formState } = formMethods;
  const overrideDirty = formMethods.getFieldState(
    'isPriceOverrideEnabled',
    formState
  ).isDirty;
  const visibleDirty = formMethods.getFieldState(
    'visibleChatPrices',
    formState
  ).isDirty;
  if (overrideDirty || visibleDirty) {
    return;
  }

  // An override exists only when displayPricesOverride is non-null; a null
  // value means "no override — fall back to the Supplier-level setting", so the
  // checkbox must read unchecked in that case.
  const hasOverride = pricesVisibility.displayPricesOverride != null;
  formMethods.resetField('isPriceOverrideEnabled', {
    defaultValue: hasOverride,
  });
  formMethods.resetField('visibleChatPrices', {
    defaultValue: pricesVisibility.displayPricesOverride ?? false,
  });
};

/**
 * Baselines the SUBMITTED value of a subset of fields (partial-success commit).
 *
 * Marks only these fields clean, leaving every other (e.g. failed) field dirty
 * and retryable. Any field whose live value diverged from the submitted value
 * since save started keeps its live value and stays dirty.
 */
export const commitSubmittedFields = (
  formMethods: SettingsForm,
  data: ChatSettingsFormData,
  fields: SettingsFieldName[]
): void => {
  fields.forEach((field) => {
    const live = formMethods.getValues(field);
    formMethods.resetField(field, { defaultValue: data[field] });
    if (!valuesEqual(live, data[field])) {
      formMethods.setValue(field, live, { shouldDirty: true });
    }
  });
};

/**
 * Baselines the whole form to the SUBMITTED values (all-success reset).
 *
 * The submitted snapshot becomes the clean baseline; any edit typed mid-save
 * stays visible and dirty so it is not dropped while being shown as saved.
 */
export const resetToSubmitted = (
  formMethods: SettingsForm,
  data: ChatSettingsFormData
): void => {
  const live = formMethods.getValues();
  formMethods.reset(data);
  SETTINGS_FIELD_NAMES.forEach((field) => {
    if (!valuesEqual(live[field], data[field])) {
      formMethods.setValue(field, live[field], { shouldDirty: true });
    }
  });
};
